import React from 'react';
import { CaretDownIcon, MeatballMenuIcon } from '@/components/Icons';
import cn from '@/utils/cn';

const NavigationClass = cn([
  'flex',
  'cursor-pointer',
  'items-center',
  'justify-center',
  'px-3',
  'py-1',
  'bg-white',
  'rounded',
  'border',
  'border-gray-200',
  'text-[#AAAAAA]',
  'hover:bg-emerge-base-2'
]);

const PageNumberClass = cn([
  'cursor-pointer',
  'flex',
  'items-center',
  'justify-center',
  'px-3',
  'py-1',
  'mx-0.5',
  'rounded',
  'text-sm',
  'font-medium',
  'text-emerge-navy-2',
  'hover:bg-emerge-base-2'
]);

const ActivePageClass = cn([
  'border-b-2',
  'border-emerge-teal',
  'rounded-none',
  'hover:bg-transparent',
  'text-emerge-teal'
]);

const PreviousNav = (props: { handlePageChange: () => void }) => (
  <li>
    <a onClick={props.handlePageChange} className={NavigationClass}>
      <span className="sr-only">Previous</span>
      <CaretDownIcon className="rotate-90" />
    </a>
  </li>
);

const NextNav = (props: { handlePageChange: () => void }) => (
  <li>
    <a onClick={props.handlePageChange} className={NavigationClass}>
      <span className="sr-only">Next</span>
      <CaretDownIcon className="-rotate-90" />
    </a>
  </li>
);

const Ellipsis = (side: string) => (
  <li key={`${side}-ellipsis`}>
    <a className={cn(PageNumberClass, 'hover:bg-none')}>
      <MeatballMenuIcon />
    </a>
  </li>
);

interface DataTableFooterOldProps {
  onPageChange: (p: number) => void;
  pageSize: number;
  totalItemsCount: number;
  currentPage: number;
  currentPageItemsCount: number;
  itemLabel: string;
}

const DataTableFooterOld: React.FC<DataTableFooterOldProps> = ({
  pageSize,
  totalItemsCount,
  currentPage,
  onPageChange,
  currentPageItemsCount,
  itemLabel
}) => {
  const numberOfPages = Math.ceil(totalItemsCount / pageSize);
  const hasMoreThanMaximumPage = totalItemsCount > pageSize;
  const isActive = (index: number) => currentPage === index;

  const handlePageChange = (p: number) => {
    if (p >= 1 && p <= numberOfPages) {
      onPageChange(p);
    }
  };

  const showPageItemsFunction = () => {
    const data = [];
    const numPage = 5;
    if (numberOfPages <= numPage) {
      data.push(
        <PreviousNav key="prev" handlePageChange={() => handlePageChange(currentPage - 1)} />
      );
      for (let i = 1; i <= numberOfPages; i++) {
        data.push(
          <li key={i}>
            <a
              onClick={() => onPageChange(i)}
              aria-current="page"
              className={cn(PageNumberClass, isActive(i) ? ActivePageClass : '')}>
              {i}
            </a>
          </li>
        );
      }
      data.push(<NextNav key="next" handlePageChange={() => handlePageChange(currentPage + 1)} />);
    } else {
      const leftside = currentPage - numPage / 2 > 1;
      const rightside = currentPage + numPage / 2 < numberOfPages;
      data.push(
        <PreviousNav key="prev" handlePageChange={() => handlePageChange(currentPage - 1)} />
      );
      if (leftside) {
        data.push(Ellipsis('left'));
      }
      const str = Math.max(1, Math.round(currentPage - numPage / 2));
      const end = Math.min(numberOfPages, Math.round(currentPage + numPage / 2));
      for (let i = str; i < end; i++) {
        data.push(
          <li key={i}>
            <a
              onClick={() => onPageChange(i)}
              aria-current="page"
              className={cn(PageNumberClass, isActive(i) ? ActivePageClass : '')}>
              {i}
            </a>
          </li>
        );
      }
      if (rightside) {
        data.push(Ellipsis('right'));
      }
      data.push(
        <li key={numberOfPages}>
          <a
            onClick={() => onPageChange(numberOfPages)}
            aria-current="page"
            className={cn(PageNumberClass, isActive(numberOfPages) ? ActivePageClass : '')}>
            {numberOfPages}
          </a>
        </li>
      );
      data.push(<NextNav key="next" handlePageChange={() => handlePageChange(currentPage + 1)} />);
    }
    return data;
  };

  return (
    <div className="w-full py-4 px-4 pr-6 flex items-center justify-between bg-emerge-base-3 border-b border-l border-r border-aux-gray rounded-b-2xl">
      <div className="flex items-center gap-1">
        <div className="text-xs font-medium flex items-center gap-x-1">
          <span className="text-emerge-navy-2">
            {currentPageItemsCount} {itemLabel}
          </span>
        </div>

        {hasMoreThanMaximumPage && (
          <nav>
            <ul className="flex items-center gap-x-2 text-sm">{showPageItemsFunction()}</ul>
          </nav>
        )}
      </div>

      <div />
    </div>
  );
};

export default DataTableFooterOld;
